import * as React from "react";
import {
  AccessReportProviderSetCopyCollectionModel,
  AccessReportProviderSetCopyResponseModel,
} from "@app/access-reports/admin/";
import { Message, MessageType } from "@app/notifications";
import { AccessReportMaintJobStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { ColumnConfig, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { cloneDeep } from "lodash";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { JSX } from "react";
import { NotificationContext } from "@app/notifications/NotificationContext";
import { QESButton } from "@app/shared/qes-standards/Button";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportProviderSetCopyModel } from "@app/access-reports/admin/shared/AccessReportProviderSetCopyModel";
import { AccessReportsProviderSetCopyDashboardResponseModel } from "@app/access-reports/admin/shared/AccessReportsProviderSetCopyDashboardResponseModel";

class CacheProviderSetsWaffleToppings {
  public key: string;
  public providerSetName: string;
  public status: string;

  constructor(
    key: string,
    providerSetName: string,
    status: string,
  ) {
    this.key = key;
    this.providerSetName = providerSetName;
    this.status = status;
  }
}

const CacheProviderSets: React.FC = () => {
  const notificationContext = React.useContext(NotificationContext);
  const accessReportsService = React.useRef(new AccessReportsService()).current;

  const [_loading, setLoading] = React.useState(true);
  const [offset, setOffset] = React.useState(0);
  const [providerSetCopies, setProviderSetCopies] = React.useState<AccessReportProviderSetCopyModel[]>([]);
  const [providerSetCopyResponses, setProviderSetCopyResponses] = React.useState<AccessReportProviderSetCopyResponseModel[]>([]);
  const [selectedProviderSetCopies, setSelectedProviderSetCopies] = React.useState<AccessReportProviderSetCopyModel[]>([]);
  const [sortDirection] = React.useState(SortDirection.Unknown);
  const [sortedColumn] = React.useState<keyof CacheProviderSetsWaffleToppings>(null);
  const [totalCount, setTotalCount] = React.useState(0);
  const batchCount: number = 100;

  React.useEffect(() => {
    notificationContext.subscribe(handleNotifications);

    return () => {
      notificationContext.unsubscribe(handleNotifications);
    }
  }, []);

  React.useEffect(() => {
    getProviderSets();
  }, []);

  const buildBulkActionBar = (): JSX.Element => {
    return (
      <QESButton
        className="text-info"
        disabled={!selectedProviderSetCopies.length}
        onClick={() => onCacheProviderSetsClick()}
        qesTestId="access-reports-admin__cache-provider-sets--cache"
        qesType="secondary"
      >
        Cache Selected Providers
      </QESButton>
    );
  }

  const buildWaffleToppings = (providerSets: AccessReportProviderSetCopyModel[]): CacheProviderSetsWaffleToppings[] => {
    const waffleToppings: CacheProviderSetsWaffleToppings[] = providerSets.map((p) => {
      const key: string = p.providerSetCopyId.toString();

      const providerSetName: string = p.providerSetName;

      const status: string = getStatus(p);

      return new CacheProviderSetsWaffleToppings(key, providerSetName, status);
    });

    return waffleToppings;
  }

  const getColumnConfig = (): ColumnConfig => {
    const columns: ColumnConfig = {
      providerSetName: {
        displayText: "Provider Set Name",
        noResize: true,
        testId: "providerSetName",
      },
      status: {
        displayText: "Status",
        noResize: true,
        testId: "status",
      },
    };

    return columns;
  }

  const customSort = (records: CacheProviderSetsWaffleToppings[], column: keyof CacheProviderSetsWaffleToppings, direction: string): CacheProviderSetsWaffleToppings[] => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    getProviderSets(true, column, sortDirection);

    return records;
  }

  const getProviderSets = async (
    isSorting = false,
    newSortedColumn: keyof CacheProviderSetsWaffleToppings = sortedColumn,
    newSortDirection: SortDirection = sortDirection,
  ): Promise<void> => {
    let newProviderSetCopies: AccessReportProviderSetCopyModel[] = providerSetCopies;
    let newBatchCount: number = batchCount;
    let newOffset: number = offset;
    let newTotalCount: number = totalCount;

    if (isSorting && !!newSortedColumn && !!newSortDirection) {
      // if sorting, return the same amount of records that are currently displayed,
      // without the need for any offsets
      newBatchCount = newProviderSetCopies.length;
      newOffset = 0;
    }

    if (isSorting && (newSortDirection === SortDirection.None || newSortDirection === SortDirection.Unknown)) {
      // this occurs when we're undoing a sort
      newSortedColumn = null;
      newSortDirection = SortDirection.Unknown;
    }

    const providerSetCopyResponseModel: AccessReportsProviderSetCopyDashboardResponseModel = await accessReportsService.getProviderSets(newBatchCount, newOffset, newSortDirection, newSortedColumn);

    if (providerSetCopyResponseModel) {
      newTotalCount = providerSetCopyResponseModel.totalCount;

      if (isSorting) {
        // sorting, meaning we should just replace the old records with the new ones
        newProviderSetCopies = providerSetCopyResponseModel.providerSetCopies;
      } else {
        // acquiring more records (either on load, or on load more click),
        // so the new batch needs to be appended
        newProviderSetCopies = newProviderSetCopies.concat(providerSetCopyResponseModel.providerSetCopies);
      }
    }

    setLoading(false);
    setOffset(newOffset + newBatchCount);
    setProviderSetCopies(newProviderSetCopies);
    setTotalCount(newTotalCount);
  }

  const getStatus = (providerSetCopy: AccessReportProviderSetCopyModel) => {
    const copiedResponse: AccessReportProviderSetCopyResponseModel = providerSetCopyResponses.find(x => x.providerSetCopyId === providerSetCopy.providerSetCopyId);

    if (!!copiedResponse?.errorMessage) {
      return copiedResponse.errorMessage;
    } else {
      return providerSetCopy.status;
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    const newSelectedProviderSetCopies: AccessReportProviderSetCopyModel[] = providerSetCopies.filter((x) => selectedKeys.includes(`${x.providerSetCopyId.toString()}`));

    setSelectedProviderSetCopies(newSelectedProviderSetCopies);
  }

  const handleNotifications = async (message: Message): Promise<void> => {
    if (message?.notificationType === MessageType.AccessReportsProviderSetCopyStatusChange) {
      const payload: AccessReportMaintJobStatusChangeNotificationMessagePayload = message.payload as AccessReportMaintJobStatusChangeNotificationMessagePayload;

      if (!!payload) {
        const newProviderSetCopies: AccessReportProviderSetCopyModel[] = cloneDeep(providerSetCopies);
        const providerSetCopy: AccessReportProviderSetCopyModel = newProviderSetCopies.find(x => x.providerSetCopyId === payload.providerSetCopyId);

        if (!!providerSetCopy) {
          providerSetCopy.status = payload.jobStatus;
        }

        setProviderSetCopies(newProviderSetCopies);
      }
    }
  }

  const onCacheProviderSetsClick = async (): Promise<void> => {
    const providerSetCopyCollectionModel: AccessReportProviderSetCopyCollectionModel = {
      providerSetCopyIds: selectedProviderSetCopies.map((x: AccessReportProviderSetCopyModel) => x.providerSetCopyId),
    };

    const newProviderSetCopyResponses: AccessReportProviderSetCopyResponseModel[] = await accessReportsService.copyProviderSets(providerSetCopyCollectionModel);

    if (newProviderSetCopyResponses.length) {
      const newProviderSetCopies = cloneDeep(providerSetCopies);
      for (const providerSetCopy of newProviderSetCopies) {
        const response = newProviderSetCopyResponses.find(x => x.providerSetCopyId === providerSetCopy.providerSetCopyId);

        if (!!response) {
          providerSetCopy.qesJobId = response.qesJobId;
        }
      }

      setProviderSetCopies(newProviderSetCopies);
      setProviderSetCopyResponses(newProviderSetCopyResponses);
      setSelectedProviderSetCopies([]);
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <Waffles<CacheProviderSetsWaffleToppings>
            bulkActionBar={buildBulkActionBar()}
            columns={getColumnConfig()}
            customSort={customSort}
            data={buildWaffleToppings(providerSetCopies) || []}
            fullWidth={true}
            handleCheckboxChange={handleCheckboxChange}
            passThroughSorting={true}
            selectedKeys={selectedProviderSetCopies.map((sr: AccessReportProviderSetCopyModel) => sr.providerSetCopyId.toString())}
            showCheckboxes={true}
            testId="access-reports-admin__cache-provider-sets--table"
          />
        </div>
      </div>

      {(providerSetCopies.length < totalCount) && (
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <QESButton
                qesTestId="access-reports-admin__cache-provider-sets--load-more"
                className="mt-3"
                onClick={() => getProviderSets()}
              >
                Load More
              </QESButton>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default CacheProviderSets;
